/**
 * PostHog Events Module
 * Tracks funnel events directly via posthog.capture()
 *
 * Events tracked:
 * - product_added_to_cart: Product added to cart (art print + readymade frame)
 * - customization_started: SKU picker modal opened
 * - customization_completed: SKU picker selection confirmed
 * - style_preset_selected: User picks a preset style card
 * - style_sheet_opened: User opens All Styles / Custom Style sheet
 * - style_sheet_tab_switched: User switches between All Styles ↔ Custom Style
 * - style_custom_applied: User applies a custom configuration
 * - size_changed: User changes size (art print + readymade frame)
 * - unit_toggled: User switches cm/in unit (both PDPs)
 * - frame_color_changed: User picks a frame colour (readymade frame)
 * - glazing_changed: User picks a glazing option (readymade frame)
 * - border_changed: User picks a border/mount option (readymade frame)
 * - photo_uploaded: User uploads a photo (readymade frame)
 * - advisor_opened: User opens the Art Advisor (both PDPs)
 * - room_background_selected: User picks a room background
 * - wall_color_selected: User picks a wall color
 * - about_style_viewed: User opens the "About this style" dialog
 * - added_to_wishlist: Product liked via in-house wishlist
 * - removed_from_wishlist: Product unliked via in-house wishlist
 * - klaviyo_email_captured: Klaviyo popup signup completed
 * - attentive_email_captured: Attentive popup signup completed
 * - recart_email_captured: Recart opt-in tool signup completed (US from 2026-08)
 * - recart_optin_interaction: Recart opt-in tool shown/clicked/closed — the first
 *   IMPRESSION signal any provider has given us, so signup rate can finally be
 *   measured per impression instead of per ≥20s-dwell session
 * - b2b_form_submitted: B2B enquiry form on art-for-business page
 * - cart_line_missing_details: a print line in the cart has no _prodigi_sku
 *   (snippets/cart-integrity-data.liquid); once per line key per browser.
 *   checkout_started carries art_lines / art_lines_missing_details / cart_token
 *   from the same data. cart_token is always a hash, never the token.
 * - cart_missing_details_note_shown / _clicked: the in-cart note on such a line
 *   was on screen / its "Choose size and frame" link was clicked; shown is once
 *   per line key per page view. cart_line_replaced: what the product page's
 *   replace flow decided (assets/cart-replace-line.js).
 *
 * Post-click UX instrumentation (2026-07):
 * - collection_product_clicked: product card click on a collection page, with grid
 *   position and section (featured hero_products zone vs main grid)
 * - gallery_interacted: PDP media gallery interaction (slide change / view-mode
 *   pill / zoom) — zoom/view_mode deduped per pageview; slide_changed deduped
 *   per SLIDE identity (slide_role/slide_kind props) so slide reach is
 *   measurable, capped at GALLERY_MAX_EVENTS per pageview
 * - configurator_seen: size/frame configurator entered the viewport on a PDP —
 *   25% session sample (impression denominator for "saw vs used")
 * - size_sheet_opened / size_sheet_applied: the "View all N sizes" sheet —
 *   open + choose-from-sheet (distinct from inline size_changed taps)
 * - rage_click: 3+ pointerdowns within 800ms in a 30px radius — capped 2/pageview
 * - product_added_to_cart also carries the configured state (size/framing/border
 *   URL params) + whether it changed since landing
 *
 * Configurator-v2 A/B instrumentation (2026-08) — everything here is ARM-NEUTRAL:
 * it reads the DOM and the confirmed variant, never a flag or a theme setting, so
 * the same code measures the legacy picker (control) and configurator v2
 * (treatment) without either arm needing its own analysis path.
 * - format_changed: print <-> canvas medium switch, both arms (canvas uptake)
 * - config_ui ('v2' | 'legacy') on the configurator event family — proves which
 *   UI actually rendered, and so detects an arm/settings mismatch
 * - product_added_to_cart also carries format, no_commitment, no_commitment_kind
 * - configurator_seen is triggered on a height-independent rule (see there) so
 *   two configurators of different heights are comparable
 *
 * Grid discovery (2026-09) — assets/grid-discovery-events.js, arm-neutral:
 * - grid_continue / grid_card_clicked / grid_click_landed, and grid_source_*
 *   props on product_added_to_cart (merged in below). grid_card_clicked is the
 *   cross-surface superset of collection_product_clicked, which is kept
 *   unchanged for existing dashboards — never sum the two.
 */

(function () {
  // Ensure PostHog is available
  const getPostHog = () => window.posthog;

  const isPostHogReady = () => {
    const ph = getPostHog();
    return ph && typeof ph.capture === 'function';
  };

  // PDP variant — always 'new' now that the new PDP is graduated.
  const getPdpVariant = () => 'new';

  // PDP type — 'art_print' or 'readymade_frame' based on page template
  const getPdpType = () =>
    document.getElementById('RMFrameProductData') ? 'readymade_frame' : 'art_print';

  // Product ID from the DOM (PDP pages)
  const getProductId = () => {
    const el = document.querySelector('[data-product-id]');
    return el?.dataset.productId || null;
  };

  // Configured state from the PDP URL params (the configurator maintains these
  // via history.replaceState — see pdp-surgical-config.js / readymade-frame-pdp.js).
  const CONFIG_PARAMS = ['size', 'framing', 'border', 'frame-color', 'glazing'];
  const getConfigState = () => {
    const params = new URLSearchParams(window.location.search);
    /** @type {Record<string, string | null>} */
    const out = {};
    for (const key of CONFIG_PARAMS) out[key] = params.get(key);
    return out;
  };
  const initialConfigState = JSON.stringify(getConfigState());

  // ---------- ARM-NEUTRAL CONFIGURATOR DESCRIPTORS ----------
  // Both PDP configurators (legacy hybrid picker and configurator v2) drive the
  // same sku-picker and the same URL params, so these read identically in both
  // A/B arms. Nothing here reads a feature flag or a theme setting: the DOM and
  // the confirmed variant are the ground truth of what the shopper actually saw
  // and chose, which is also what makes an arm/settings mismatch detectable.

  /** 'v2' | 'legacy' — which configurator UI rendered on this pageview. */
  const getConfigUi = () => (document.querySelector('pdp-configurator-v2') ? 'v2' : 'legacy');

  /** 'print' | 'canvas' | null — the medium, from the confirmed variant.
   *  `artProduct` is the catalogue's own field ('art-print' / 'canvas-print'),
   *  so this survives any relabelling of the UI. */
  const getFormat = () => {
    const sp = window.skuPickerInstance;
    const art = sp?.variantConfigurators?.variants?.[sp?.currentVariantId]?.artProduct;
    if (!art) return null;
    return art === 'canvas-print' ? 'canvas' : 'print';
  };

  /**
   * The no-commitment guardrail, resolved the same way in both arms.
   *
   * A prior rollout that demoted unframed prints cost a four-figure sum per day,
   * so the share of adds that take the cheapest, least-committed option is the
   * standing guardrail of any configurator change — and it has to be countable
   * without knowing which UI rendered.
   *
   * `kind` mirrors the `no_commitment_kind` on the v2 selection events:
   *   'intrinsic' — the catalogue really offers a no-commitment option here
   *                 (unframed paper, or rolled canvas once it exists).
   *   'promoted'  — nothing no-commitment exists on this medium, so the row led
   *                 with its cheapest cell instead (wrapped canvas today). The
   *                 add is NOT no-commitment; this says why the denominator is
   *                 empty rather than leaving it looking like a collapse.
   * Comparing raw no-commitment counts across formats without this split reads
   * canvas as a total collapse of the guardrail metric, by construction.
   */
  // No rolled canvas variant exists in the catalogue yet, so this branch is
  // currently unreachable BY DESIGN — it is the canvas analog of `unframed` and
  // goes live the day that variant ships. Not dead code to tidy away.
  const ROLLED_HANDLES = new Set(['rolled', 'rolled-canvas']);
  const getNoCommitment = (framing, format) => {
    if (!framing) return { no_commitment: null, no_commitment_kind: null };
    if (ROLLED_HANDLES.has(framing)) return { no_commitment: true, no_commitment_kind: 'intrinsic' };
    if (framing !== 'unframed') return { no_commitment: false, no_commitment_kind: null };
    // 'unframed' on canvas is the wrapped stretcher — a finished product, not an
    // uncommitted one. On paper it is the bare print.
    return format === 'canvas'
      ? { no_commitment: false, no_commitment_kind: 'promoted' }
      : { no_commitment: true, no_commitment_kind: 'intrinsic' };
  };

  // ---------- ADD TO CART ----------
  document.addEventListener('cart:update', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};
    const data = detail.data || {};

    // Skip if this was an error
    if (data.didError) return;

    // Only track actual additions (not removals/updates)
    const validSources = ['product-form-component', 'bundle-submission', 'readymade-frame-pdp'];
    if (!validSources.includes(data.source)) return;

    /** @type {Record<string, any>} */
    const properties = {
      product_id: data.productId || null,
      variant_id: data.variantId || null,
      quantity: data.itemCount || 1,
      source: data.source,
      cart_value_cents: data.finalLinePrice ?? null,
      currency: data.currency || null,
      pdp_variant: getPdpVariant(),
    };

    // Price from Shopify cart API is in cents — convert to decimal
    if (data.price != null) {
      properties.value = data.price / 100;
    }
    if (data.currency) {
      properties.currency = data.currency;
    }

    // Configured state at the moment of the add (which size tier / framing sells,
    // and whether the shopper configured at all before adding).
    const config = getConfigState();
    properties.size_handle = config.size;
    properties.framing = config.framing;
    properties.border = config.border;
    properties.frame_color = config['frame-color'];
    properties.config_changed = JSON.stringify(config) !== initialConfigState;

    // Medium, the no-commitment guardrail, and which UI rendered — the three
    // cuts the configurator-v2 A/B is read on, on the one event that fires
    // identically in both arms. `variant_id` can reconstruct the medium in BQ,
    // but only after a join; canvas share is a headline metric of this test and
    // should be readable in PostHog without one.
    properties.format = getFormat();
    Object.assign(properties, getNoCommitment(config.framing, properties.format));
    properties.config_ui = getConfigUi();

    // Discovery source: grid_source_* when this add is the product a grid card
    // click led to (assets/grid-discovery-events.js). Additive only — absent
    // when there is no matching click, and no existing prop changes.
    try {
      Object.assign(properties, window.fyGridSource?.atcProps?.(data) || {});
    } catch (err) {
      /* attribution is best-effort; the add event must still go */
    }

    getPostHog().capture('product_added_to_cart', properties);
  });

  // ---------- CUSTOMIZATION STARTED (SKU picker opened) ----------
  document.addEventListener('sku-picker:modal-opened', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('customization_started', {
      option_type: detail.optionType || null,
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- CUSTOMIZATION COMPLETED (SKU picker selection confirmed) ----------
  document.addEventListener('sku-picker:selection-confirmed', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('customization_completed', {
      option_type: detail.optionType || null,
      product_id: detail.productId || null,
      variant_id: detail.variantId || null,
      selection: detail.selection || null,
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- STYLE PRESET SELECTED ----------
  document.addEventListener('style-picker:preset-selected', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('style_preset_selected', {
      config_ui: getConfigUi(),
      preset_id: detail.presetId || null,
      preset_name: detail.presetName || null,
      framing: detail.framing || null,
      frame_color: detail.frameColor || null,
      border: detail.border || null,
      source: detail.source || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- STYLE SHEET OPENED ----------
  document.addEventListener('style-picker:sheet-opened', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('style_sheet_opened', {
      config_ui: getConfigUi(),
      initial_tab: detail.initialTab || null,
      // 'surgical-frames-drawer' when the surgical PDP's "View all N frames"
      // drawer dispatched it (2026-07); absent/null = legacy style-picker sheet.
      surface: detail.surface || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- SIZE SHEET OPENED / APPLIED ----------
  // The surgical PDP's "View all N sizes" sheet (true-to-scale room mock).
  // Opened = engagement with the long-tail size range; applied = a size chosen
  // FROM the sheet (inline picker taps fire size_changed with no sheet signal).
  document.addEventListener('pdp:size-sheet-opened', (e) => {
    if (!isPostHogReady()) return;
    getPostHog().capture('size_sheet_opened', {
      config_ui: getConfigUi(),
      selected_size: e.detail?.selected || null,
      product_id: getProductId(),
      pdp_type: getPdpType(),
      pdp_variant: getPdpVariant(),
    });
  });

  document.addEventListener('pdp:size-sheet-applied', (e) => {
    if (!isPostHogReady()) return;
    getPostHog().capture('size_sheet_applied', {
      config_ui: getConfigUi(),
      size_handle: e.detail?.handle || null,
      product_id: getProductId(),
      pdp_type: getPdpType(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- STYLE SHEET TAB SWITCHED ----------
  document.addEventListener('style-picker:tab-switched', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('style_sheet_tab_switched', {
      from_tab: detail.fromTab || null,
      to_tab: detail.toTab || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- STYLE CUSTOM APPLIED ----------
  document.addEventListener('style-picker:custom-applied', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('style_custom_applied', {
      variant_id: detail.variantId || null,
      framing: detail.framing || null,
      frame_color: detail.frameColor || null,
      border: detail.border || null,
      product_type: detail.productType || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- SIZE CHANGED (art print PDP) ----------
  // Fires in both A/B arms: the legacy picker and configurator v2 both dispatch
  // `size-impact:changed`, so size mix stays comparable across arms even though
  // the two rows offer different size sets.
  document.addEventListener('size-impact:changed', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('size_changed', {
      size_handle: detail.handle || null,
      product_id: getProductId(),
      pdp_type: 'art_print',
      pdp_variant: getPdpVariant(),
      format: getFormat(),
      config_ui: getConfigUi(),
    });
  });

  // ---------- FORMAT CHANGED (print <-> canvas, both arms) ----------
  // Canvas is a variant switch in every PDP UI we have shipped, so the medium
  // change is observable identically in both arms — the legacy PDP reaches it
  // through the "Wrapped Canvas" style card, configurator v2 through the format
  // step it promotes to the first decision. Emitting one event for both is what
  // makes "did promoting format move canvas uptake?" a funnel question rather
  // than an add-to-cart-only one.
  //
  // Deduped against the last observed medium: `sku-picker:variant-changed`
  // fires on every variant change (colour/size resolution included), and only
  // the transitions between media are format changes.
  //
  // `lastFormat` is null until the picker has booted, and a null -> medium
  // transition is the boot itself, never a shopper action: it is recorded and
  // not reported. Without that guard every PDP would emit a format change on
  // first variant load and canvas uptake would read as universal.
  //
  // The seed below is what makes the guard safe. sku-picker boots AFTER this
  // module evaluates, so `getFormat()` is null here — and without seeding, the
  // guard would swallow the FIRST medium change of the pageview, which on a
  // test about promoting format is the single most important event on the page.
  // Verified against both arms: pre-seed, the first switch emitted nothing and
  // only the switch back was reported. `sku-picker:ready` is dispatched on
  // WINDOW, not document (sku-picker.js), and is the earliest moment the
  // confirmed medium is knowable.
  let lastFormat = getFormat();
  window.addEventListener('sku-picker:ready', () => {
    if (lastFormat === null) lastFormat = getFormat();
  });
  document.addEventListener('sku-picker:variant-changed', () => {
    const next = getFormat();
    if (!next || next === lastFormat) return;
    const previous = lastFormat;
    lastFormat = next;
    if (previous === null) return;
    if (!isPostHogReady()) return;
    getPostHog().capture('format_changed', {
      from: previous,
      to: next,
      product_id: getProductId(),
      pdp_type: getPdpType(),
      pdp_variant: getPdpVariant(),
      config_ui: getConfigUi(),
    });
  });

  // ---------- SIZE CHANGED (readymade frame PDP) ----------
  document.addEventListener('rmf:size-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('size_changed', {
      size_handle: detail.handle || null,
      product_id: detail.productId || getProductId(),
      pdp_type: 'readymade_frame',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- FRAME COLOUR CHANGED (readymade frame PDP) ----------
  document.addEventListener('rmf:color-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('frame_color_changed', {
      color_handle: detail.handle || null,
      color_name: detail.colorName || null,
      product_id: detail.productId || getProductId(),
      pdp_type: 'readymade_frame',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- GLAZING CHANGED (readymade frame PDP) ----------
  document.addEventListener('rmf:glazing-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('glazing_changed', {
      glazing_handle: detail.handle || null,
      glazing_name: detail.glazingName || null,
      product_id: detail.productId || getProductId(),
      pdp_type: 'readymade_frame',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- BORDER/MOUNT CHANGED (readymade frame PDP) ----------
  document.addEventListener('rmf:border-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('border_changed', {
      border_handle: detail.handle || null,
      product_id: detail.productId || getProductId(),
      pdp_type: 'readymade_frame',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- PHOTO UPLOADED (readymade frame PDP) ----------
  document.addEventListener('rmf:photo-uploaded', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('photo_uploaded', {
      product_id: detail.productId || getProductId(),
      pdp_type: 'readymade_frame',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- UNIT TOGGLED (both PDPs) ----------
  document.addEventListener('sku-picker:unit-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('unit_toggled', {
      unit: detail.unit || null,
      product_id: getProductId(),
      pdp_type: getPdpType(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- ADVISOR OPENED (both PDPs) ----------
  document.addEventListener('whisper:open-chat', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('advisor_opened', {
      intent: detail.intent || null,
      product_handle: detail.productHandle || null,
      product_id: getProductId(),
      pdp_type: getPdpType(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- ROOM BACKGROUND SELECTED ----------
  document.addEventListener('room-background:changed', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    // Skip automatic room switches (e.g. triggered by size change)
    if (detail.isAutoSwitch) return;

    getPostHog().capture('room_background_selected', {
      room_index: detail.index ?? null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- WALL COLOR SELECTED ----------
  document.addEventListener('wall-color-change', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('wall_color_selected', {
      wall_color_hex: detail.hex || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- KLAVIYO SIGNUP COMPLETED ----------
  // Klaviyo multi-step forms dispatch per step. We debounce by form_id
  // with a 30s window (enough time to complete a phone step), and flush
  // immediately on page unload so we don't lose the event if they navigate.
  const klaviyoPending = {}; // form_id → { timeout, props }

  function flushKlaviyoEvents() {
    if (!isPostHogReady()) return;
    for (const formId of Object.keys(klaviyoPending)) {
      getPostHog().capture('klaviyo_email_captured', klaviyoPending[formId].props);
      delete klaviyoPending[formId];
    }
  }

  document.addEventListener('klaviyo:signup-completed', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};
    const formId = detail.form_id || 'unknown';
    const props = {
      source: detail.source || 'popup',
      has_email: !!detail.email,
      has_phone: !!detail.phone,
      form_id: formId,
    };

    if (klaviyoPending[formId]) clearTimeout(klaviyoPending[formId].timeout);
    klaviyoPending[formId] = {
      props: props,
      timeout: setTimeout(() => {
        delete klaviyoPending[formId];
        getPostHog().capture('klaviyo_email_captured', props);
      }, 30000),
    };
  });

  window.addEventListener('beforeunload', flushKlaviyoEvents);

  // ---------- ATTENTIVE SIGNUP COMPLETED ----------
  document.addEventListener('attentive:signup-completed', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};
    getPostHog().capture('attentive_email_captured', {
      source: detail.source || 'popup',
      has_email: !!detail.email,
      has_phone: !!detail.phone,
      form_id: detail.form_id || 'unknown',
    });
  });

  // ---------- RECART OPT-IN TOOL ----------
  // recart_email_captured mirrors attentive_email_captured field-for-field so the
  // warehouse can union the three providers into one has_email_capture flag. Same
  // semantics too: one event per signup carrying has_email/has_phone booleans, and
  // never the address itself — no PII reaches PostHog from any capture surface.
  document.addEventListener('recart:signup-completed', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};
    getPostHog().capture('recart_email_captured', {
      source: detail.source || 'popup',
      has_email: !!detail.email,
      has_phone: !!detail.phone,
      form_id: detail.form_id || 'unknown',
      optin_tool_name: detail.optin_tool_name || null,
      experiment_id: detail.experiment_id || null,
      experiment_name: detail.experiment_name || null,
    });
  });

  // The impression leg. This is the first provider to tell us a unit was actually
  // SHOWN, which is what turns the signup rate from "captures / sessions that
  // dwelled ≥20s" (a proxy) into a real funnel: shown → clicked → captured.
  //
  // Deduped per (tool, interaction) per pageview. Recart should not repeat an
  // impression, but this is an unbounded listener on a third-party event and the
  // question we need answered — "was this unit served at all in this session" —
  // is satisfied by the first occurrence.
  const recartInteractionsSeen = new Set();
  document.addEventListener('recart:optin-interaction', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};
    const formId = detail.form_id || 'unknown';
    const key = formId + '|' + detail.interaction;
    if (recartInteractionsSeen.has(key)) return;
    recartInteractionsSeen.add(key);

    getPostHog().capture('recart_optin_interaction', {
      interaction: detail.interaction,
      form_id: formId,
      optin_tool_name: detail.optin_tool_name || null,
      experiment_id: detail.experiment_id || null,
      experiment_name: detail.experiment_name || null,
    });
  });

  // ---------- B2B FORM SUBMITTED ----------
  document.addEventListener('b2b:form-submitted', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('b2b_form_submitted', {
      source: detail.source || 'b2b_form',
      has_email: !!detail.email,
      has_phone: !!detail.phone,
      has_company: !!detail.company,
      form_id: detail.form_id || 'b2b-art-for-business',
    });
  });

  // ---------- ABOUT STYLE VIEWED ----------
  document.addEventListener('style-picker:about-opened', (e) => {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};

    getPostHog().capture('about_style_viewed', {
      preset_id: detail.presetId || null,
      preset_name: detail.presetName || null,
      product_id: getProductId(),
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- WISHLIST LIKE / UNLIKE ----------
  function handleWishlistEvent(e) {
    if (!isPostHogReady()) return;

    const detail = e.detail || {};
    if (detail.event === 'added_to_wishlist' || detail.event === 'removed_from_wishlist') {
      getPostHog().capture(detail.event, {
        product_handle: detail.product_handle || null,
        product_id: detail.product_id || null,
        source: detail.source || null,
      });
    }
  }
  // Listen for both: 'wishlist:analytics' is canonical, 'custom-analytics' is
  // the old name still served from CDN cache until wishlist-state.js cache expires
  document.addEventListener('wishlist:analytics', handleWishlistEvent);
  document.addEventListener('custom-analytics', handleWishlistEvent);

  // ---------- VIEW SIMILAR CLICKED ----------
  document.addEventListener('click', function(e) {
    var btn = e.target.closest('.view-similar--v2, [data-click-event="tapped-view-similar"]');
    if (!btn || !isPostHogReady()) return;
    var handle = window.location.pathname.split('/products/')[1];
    if (handle) handle = handle.split('?')[0];
    getPostHog().capture('view_similar_clicked', {
      product_handle: handle || '',
      pdp_variant: getPdpVariant(),
    });
  });

  // ---------- NAV MENU OPENED ----------
  document.addEventListener('nav:menu-opened', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('nav_menu_opened', {
      menu: detail.menu || null,
      menu_label: detail.label || null,
      device: detail.device || null,
    });
  });

  // ---------- NAV LINK CLICKED ----------
  document.addEventListener('nav:link-clicked', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('nav_link_clicked', {
      menu: detail.menu || null,
      device: detail.device || null,
      url: detail.url || null,
      link_label: detail.label || null,
    });
  });

  // ============================================================
  //  CART & CHECKOUT FUNNEL
  // ============================================================

  // ---------- CART DRAWER OPENED ----------
  document.addEventListener('cart:drawer-opened', () => {
    if (!isPostHogReady()) return;
    getPostHog().capture('cart_viewed', { source: 'drawer' });
  });

  // ---------- CART PAGE VIEWED ----------
  if (document.querySelector('.cart-form, [data-cart-page]') && /\/cart\b/.test(window.location.pathname)) {
    if (isPostHogReady()) {
      getPostHog().capture('cart_viewed', { source: 'page' });
    }
  }

  // ---------- PRINT LINE MISSING ITS DETAILS (cart_line_missing_details) ----------
  // A print line in the cart without `_prodigi_sku` cannot be sent to Prodigi
  // and is charged the base price. snippets/cart-integrity-data.liquid renders
  // the cart's print lines into #fy-cart-integrity on page load; this fires one
  // event per bare line key per browser, with enough context to say how the line
  // got there (spec: desks/storefront/specs/2026-11-03-bare-cart-lines.md, E1).
  //
  // No personal data. cart_token is the first 16 hex characters of the SHA-256
  // of the cart token WITHOUT its ?key= secret, never the token: the token alone
  // opens the cart (spec 4b, d12). Liquid's cart.token renders blank, so it is
  // read from the `cart` cookie and hashed here. BigQuery join to orders:
  //   SUBSTR(TO_HEX(SHA256(orders.cart_token)), 1, 16) = cart_token
  const cartIntegrity = (() => {
    try {
      const el = document.getElementById('fy-cart-integrity');
      return el ? JSON.parse(el.textContent) : null;
    } catch (err) {
      return null;
    }
  })();

  const readCartCookieToken = () => {
    try {
      const m = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
      return m ? decodeURIComponent(m[1]).split('?')[0] || null : null;
    } catch (err) {
      return null;
    }
  };
  // Kept current for checkout_started, which captures synchronously on click:
  // recomputed on load and after every cart change (the first add creates the
  // cookie). null when there is no cart cookie or no crypto.subtle.
  let cartTokenHash = null;
  const refreshCartTokenHash = async () => {
    const token = readCartCookieToken();
    if (!token || !window.crypto || !window.crypto.subtle) {
      cartTokenHash = null;
      return null;
    }
    try {
      const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
      cartTokenHash = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
    } catch (err) {
      cartTokenHash = null;
    }
    return cartTokenHash;
  };
  const cartTokenHashReady = refreshCartTokenHash();
  // Any cart change on this page makes the page-load counts below stale (a bare
  // line removed in the drawer would still be counted); checkout_started says so.
  let cartChangedSinceLoad = false;
  document.addEventListener('cart:update', () => {
    cartChangedSinceLoad = true;
    refreshCartTokenHash();
  });
  ['cart:item-removed', 'cart:quantity-changed'].forEach((type) =>
    document.addEventListener(type, () => {
      cartChangedSinceLoad = true;
    })
  );

  // Storage is named, not passed: merely reading window.localStorage throws
  // (SecurityError) with cookies blocked, and a throw at this level would kill
  // every listener below it in this file.
  const readStored = (storageName, key, fallback) => {
    try {
      const raw = window[storageName].getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (err) {
      return fallback;
    }
  };
  const writeStored = (storageName, key, value) => {
    try {
      window[storageName].setItem(key, JSON.stringify(value));
    } catch (err) {
      /* storage blocked: dedupe degrades to per-pageview */
    }
  };

  // Campaign context. The bare line is often seen a page or two after the
  // landing, so the landing's utm / Attentive params are kept for the session.
  // attn_macro is base64 of the Attentive macro the link came from (e.g.
  // "triggerEvent.product.link"); externalId and other params are never kept.
  const LANDING_KEY = 'fy_landing_campaign';
  const campaignFromUrl = (search) => {
    const params = new URLSearchParams(search || '');
    const hasUtm = ['utm_source', 'utm_medium', 'utm_campaign'].some((k) => params.has(k));
    let hasAttn = false;
    params.forEach((_v, k) => {
      if (k.startsWith('attn_')) hasAttn = true;
    });
    if (!hasUtm && !hasAttn) return null;
    let attnMacro = null;
    const rawMacro = params.get('attn_macro');
    if (rawMacro) {
      try {
        const decoded = atob(rawMacro);
        attnMacro = /^[\w.\[\]-]{1,80}$/.test(decoded) ? decoded : null;
      } catch (err) {
        attnMacro = null;
      }
    }
    return {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      has_attn_params: hasAttn,
      attn_macro: attnMacro,
    };
  };
  const pageCampaign = campaignFromUrl(window.location.search);
  if (pageCampaign) writeStored('sessionStorage', LANDING_KEY, pageCampaign);

  // This browser's view of the cart over time: when it first saw this cart
  // token, whether it had a different cart before, and which print variants it
  // has seen WITH their details. Updated on every page view with print lines.
  const CART_SEEN_KEY = 'fy_cart_seen';
  const DETAILED_VARIANTS_KEY = 'fy_detailed_variants';
  const BARE_SENT_KEY = 'fy_bare_lines_sent';
  let cartSeen = null;
  let detailedBefore = [];
  const updateCartHistory = (tokenHash) => {
    if (tokenHash) {
      const prevSeen = readStored('localStorage', CART_SEEN_KEY, null);
      cartSeen = prevSeen && prevSeen.t === tokenHash
        ? prevSeen
        : { t: tokenHash, at: Date.now(), changed: !!(prevSeen && prevSeen.t) };
      writeStored('localStorage', CART_SEEN_KEY, cartSeen);
    }
    const stored = readStored('localStorage', DETAILED_VARIANTS_KEY, []);
    detailedBefore = Array.isArray(stored) ? stored : [];
    const detailedNow = Array.isArray(cartIntegrity.d) ? cartIntegrity.d : [];
    const merged = [...new Set([...detailedBefore, ...detailedNow])].slice(-200);
    writeStored('localStorage', DETAILED_VARIANTS_KEY, merged);
  };

  const bareLineProps = (line) => {
    const detailed = Array.isArray(cartIntegrity.d) ? cartIntegrity.d : [];
    const campaign = pageCampaign || readStored('sessionStorage', LANDING_KEY, null) || {};
    let referrerHost = null;
    try {
      referrerHost = document.referrer ? new URL(document.referrer).hostname : null;
    } catch (err) {
      referrerHost = null;
    }
    return {
      line_key: line.k,
      variant_id: line.v,
      product_handle: line.h,
      quantity: line.q,
      cart_token: cartTokenHash,
      page_path: window.location.pathname,
      page_type: cartIntegrity.pt || null,
      referrer_host: referrerHost,
      utm_source: campaign.utm_source || null,
      utm_medium: campaign.utm_medium || null,
      utm_campaign: campaign.utm_campaign || null,
      has_attn_params: !!campaign.has_attn_params,
      attn_macro: campaign.attn_macro || null,
      cart_first_seen_min: cartSeen ? Math.round((Date.now() - cartSeen.at) / 60000) : null,
      cart_token_changed: !!(cartSeen && cartSeen.changed),
      had_details_same_variant_before: detailedBefore.includes(line.v) || detailed.includes(line.v),
      other_art_lines_with_details: detailed.length,
      recart_attr_present: !!cartIntegrity.r,
      logged_in: !!cartIntegrity.li,
    };
  };

  // Fire after the PostHog interaction gate (snippets/posthog.liquid) would
  // let the event through AND after its super-props are registered, and only
  // then mark the line as sent. Marking earlier would lose the line for good
  // on a view with no interaction, whose buffered events are never sent.
  const HUMAN_SIGNALS = ['pointermove', 'pointerdown', 'scroll', 'keydown', 'touchstart', 'wheel'];
  const fireBareLines = () => {
    const missing = Array.isArray(cartIntegrity.m) ? cartIntegrity.m : [];
    if (!missing.length) return;
    const sent = readStored('localStorage', BARE_SENT_KEY, []);
    const sentList = Array.isArray(sent) ? sent : [];
    const fresh = missing.filter((line) => line && line.k && !sentList.includes(line.k));
    if (!fresh.length) return;
    let human = false;
    let ready = !!window.__fyPosthogReady;
    let hashed = false;
    let done = false;
    const go = () => {
      if (done || !human || !ready || !hashed || !isPostHogReady()) return;
      done = true;
      fresh.forEach((line) => getPostHog().capture('cart_line_missing_details', bareLineProps(line)));
      writeStored('localStorage', BARE_SENT_KEY, [...sentList, ...fresh.map((l) => l.k)].slice(-50));
    };
    const onHuman = () => {
      human = true;
      HUMAN_SIGNALS.forEach((s) => document.removeEventListener(s, onHuman, true));
      go();
    };
    HUMAN_SIGNALS.forEach((s) => document.addEventListener(s, onHuman, { capture: true, passive: true }));
    if (!ready) {
      document.addEventListener('fy:posthog-ready', () => {
        ready = true;
        go();
      }, { once: true });
    }
    cartTokenHashReady.then(() => {
      hashed = true;
      go();
    });
  };
  if (cartIntegrity) {
    cartTokenHashReady.then((tokenHash) => updateCartHistory(tokenHash));
    fireBareLines();
  }

  // ---------- BARE-LINE NOTE SHOWN / CLICKED (spec d18, horizon #288) ----------
  // snippets/cart-products.liquid shows "We're missing your size and frame for
  // this print" under a print line without `_prodigi_sku`. E1 above
  // (cart_line_missing_details) says a bare line was IN the cart at page load;
  // these say the note was actually on screen, and that it was clicked.
  //
  // _shown fires when at least half the note is in the viewport
  // (IntersectionObserver), so a note inside the closed drawer does not count
  // until the drawer opens. Dedupe: once per line key per page view (in
  // memory), so drawer re-renders and re-opens on the same page don't repeat
  // it; a new page view where the shopper sees it again fires again.
  // Captures go through the interaction gate in snippets/posthog.liquid like
  // every theme event: shoppers who never interact are never counted.
  const NOTE_SELECTOR = '[data-cart-missing-details]';
  const NOTE_LINK_SELECTOR = '.cart-items__missing-details-link';
  const toNumberOrNull = (v) => {
    if (v === undefined || v === null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const pageTypeFromPath = () => {
    const p = (window.location && window.location.pathname) || '';
    const rest = p.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '') || '/';
    if (rest === '/' ) return 'index';
    if (/^\/cart\/?$/.test(rest)) return 'cart';
    if (/\/products\//.test(rest)) return 'product';
    if (/^\/collections\//.test(rest)) return 'collection';
    return null;
  };
  const noteProps = (el) => {
    const d = (el && el.dataset) || {};
    let inDrawer = false;
    try {
      inDrawer = !!(el.closest && el.closest('cart-drawer-component, .cart-drawer'));
    } catch (err) {
      inDrawer = false;
    }
    return {
      line_key: d.lineKey || null,
      variant_id: toNumberOrNull(d.variantId),
      product_handle: d.productHandle || null,
      quantity: toNumberOrNull(d.quantity),
      surface: inDrawer ? 'drawer' : 'cart_page',
      // The page the shopper is on: a drawer re-rendered through the Section
      // Rendering API can carry another URL's template name.
      page_type: (cartIntegrity && cartIntegrity.pt) || pageTypeFromPath() || d.pageType || null,
      other_art_lines_with_details: toNumberOrNull(d.otherDetailed),
      cart_token: cartTokenHash,
    };
  };

  const noteShownKeys = new Set();
  const noteShownPending = [];
  let noteReady = !!window.__fyPosthogReady;
  let noteHashed = false;
  const flushNoteShown = () => {
    if (!noteReady || !noteHashed || !isPostHogReady()) return;
    while (noteShownPending.length) {
      const props = noteShownPending.shift();
      props.cart_token = cartTokenHash;
      getPostHog().capture('cart_missing_details_note_shown', props);
    }
  };
  if (!noteReady) {
    document.addEventListener('fy:posthog-ready', () => {
      noteReady = true;
      flushNoteShown();
    }, { once: true });
  }
  cartTokenHashReady.then(() => {
    noteHashed = true;
    flushNoteShown();
  });
  // Props are read when the note is seen, not at flush: a drawer re-render can
  // detach the element, and a detached note no longer knows its surface.
  const markNoteShown = (el) => {
    const key = el && el.dataset && el.dataset.lineKey;
    if (!key || noteShownKeys.has(key)) return;
    noteShownKeys.add(key);
    noteShownPending.push(noteProps(el));
    flushNoteShown();
  };

  const noteObserver = typeof window.IntersectionObserver === 'function'
    ? new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          noteObserver.unobserve(entry.target);
          markNoteShown(entry.target);
        });
      }, { threshold: 0.5 })
    : null;
  // Every match is (re-)observed on each scan: observe() on an observed element
  // is a no-op, and a morph can keep a node but swap its line key, which must
  // be seen again. The per-key Set above stops repeats.
  const observeNotes = () => {
    if (!noteObserver) return;
    try {
      document.querySelectorAll(NOTE_SELECTOR).forEach((el) => noteObserver.observe(el));
    } catch (err) {
      /* never break the page for analytics */
    }
  };
  observeNotes();
  // The drawer renders its lines on open and morphs them on every cart change.
  let noteScanQueued = false;
  const queueNoteScan = () => {
    if (noteScanQueued) return;
    noteScanQueued = true;
    setTimeout(() => {
      noteScanQueued = false;
      observeNotes();
    }, 50);
  };
  if (noteObserver && typeof window.MutationObserver === 'function' && document.body) {
    new window.MutationObserver(queueNoteScan).observe(document.body, { childList: true, subtree: true });
  }
  document.addEventListener('cart:update', queueNoteScan);

  // _clicked: the link navigates away, so sendBeacon like grid_card_clicked.
  document.addEventListener('click', (e) => {
    const link = e.target && e.target.closest ? e.target.closest(NOTE_LINK_SELECTOR) : null;
    if (!link || !isPostHogReady()) return;
    const note = link.closest(NOTE_SELECTOR);
    if (!note) return;
    getPostHog().capture('cart_missing_details_note_clicked', noteProps(note), { transport: 'sendBeacon' });
  }, true);

  // cart_line_replaced: product-form.js reports what the replace flow decided
  // after a successful add from the note's link (assets/cart-replace-line.js).
  document.addEventListener('cart:line-replace', (e) => {
    if (!isPostHogReady()) return;
    const detail = (e && e.detail) || {};
    if (!detail.outcome) return;
    getPostHog().capture('cart_line_replaced', {
      outcome: detail.outcome,
      line_key: detail.line_key || null,
      product_handle: detail.product_handle || null,
    });
  });

  // ---------- CART QUANTITY CHANGED ----------
  document.addEventListener('cart:quantity-changed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('cart_quantity_changed', {
      line: detail.line ?? null,
      quantity: detail.quantity ?? null,
    });
  });

  // ---------- CART ITEM REMOVED ----------
  document.addEventListener('cart:item-removed', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('cart_item_removed', {
      product_handle: detail.product_handle || null,
      product_id: detail.product_id || null,
    });
  });

  // ---------- CHECKOUT BUTTON CLICKED ----------
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('#checkout, [name="checkout"]');
    if (!btn || !isPostHogReady()) return;
    // art_lines / art_lines_missing_details describe the cart as this page was
    // rendered (#fy-cart-integrity); a line added on this page came from the
    // product form with its details, so it can only be undercounted in
    // art_lines, never in the missing count. Both are 0 when the page loaded
    // with no print lines. cart_changed_since_load = the shopper changed the cart
    // on this page, so the counts may be stale (e.g. a bare line removed).
    // cart_token is the hashed cookie token (see above).
    getPostHog().capture('checkout_started', {
      source: document.querySelector('cart-drawer-component dialog[open]') ? 'drawer' : 'page',
      art_lines: cartIntegrity ? cartIntegrity.a || 0 : 0,
      art_lines_missing_details: cartIntegrity && Array.isArray(cartIntegrity.m) ? cartIntegrity.m.length : 0,
      cart_token: cartTokenHash,
      cart_changed_since_load: cartChangedSinceLoad,
    });
  });

  // ============================================================
  //  COLLECTION BROWSING
  // ============================================================

  // ---------- COLLECTION FILTER / SORT CHANGED ----------
  document.addEventListener('filter:update', (e) => {
    if (!isPostHogReady()) return;
    const params = e.detail?.queryParams;
    if (!params) return;
    const filters = {};
    const sort = params.get('sort_by') || null;
    for (const [key, value] of params.entries()) {
      if (key !== 'sort_by' && key !== 'q') {
        filters[key] = value;
      }
    }
    getPostHog().capture('collection_filtered', {
      sort_by: sort,
      filters: Object.keys(filters).length > 0 ? filters : null,
      collection_url: window.location.pathname,
    });
  });

  // ---------- COLLECTION LOAD MORE ----------
  // There used to be a `collection:page-loaded` -> collection_page_loaded
  // listener here. Nothing ever dispatched that event, so it never fired.
  // Removed rather than wired up: grid_continue (assets/grid-discovery-events.js)
  // now records every pager load, on collections AND search / PDP / wishlist
  // grids, in both A/B arms — dispatching the old event as well would have
  // sent two events per page load for one action.

  // ============================================================
  //  SEARCH
  // ============================================================

  // ---------- SEARCH SUBMITTED (autocomplete) ----------
  document.addEventListener('search:submitted', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('search_submitted', {
      query: detail.query || null,
      source: detail.source || null,
      position: detail.position ?? null,
    });
  });

  // ---------- SEARCH RESULTS LOADED ----------
  document.addEventListener('search:results-loaded', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('search_results_loaded', {
      query: detail.query || null,
      result_count: detail.result_count ?? null,
      page: detail.page ?? null,
      execution_time_ms: detail.execution_time_ms ?? null,
    });
  });

  // ---------- SEARCH RESULT CLICKED ----------
  document.addEventListener('search:result-clicked', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('search_result_clicked', {
      query: detail.query || null,
      product_id: detail.product_id || null,
      product_handle: detail.product_handle || null,
      position: detail.position ?? null,
      page: detail.page ?? null,
    });
  });

  // ---------- SEARCH ZERO RESULTS ----------
  document.addEventListener('search:zero-results', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('search_zero_results', {
      query: detail.query || null,
      suggested_query: detail.suggested_query || null,
      execution_time_ms: detail.execution_time_ms ?? null,
    });
  });

  // ---------- HOMEPAGE: ADVISOR HERO SUBMIT ----------
  document.addEventListener('home:advisor-submit', (e) => {
    if (!isPostHogReady()) return;
    const detail = e.detail || {};
    getPostHog().capture('homepage_advisor_submitted', {
      query: detail.query || null,
      has_photo: detail.has_photo || false,
      has_url: detail.has_url || false,
    });
  });

  // ---------- HOMEPAGE: FLOATING ADVISOR CLICKED ----------
  document.addEventListener('home:floating-advisor-clicked', () => {
    if (!isPostHogReady()) return;
    getPostHog().capture('homepage_floating_advisor_clicked');
  });

  // ---------- HOMEPAGE: ROOM / CATEGORY TILE CLICKED ----------
  document.addEventListener('click', (e) => {
    if (!isPostHogReady()) return;

    // Room tile
    const roomTile = e.target.closest('.home-rooms-grid__cell a, .home-rooms-grid a');
    if (roomTile) {
      getPostHog().capture('homepage_tile_clicked', {
        tile_type: 'room',
        url: roomTile.getAttribute('href') || null,
        label: roomTile.textContent?.trim()?.substring(0, 60) || null,
      });
      return;
    }

    // Category tile
    const catTile = e.target.closest('.home-categories-grid__cell a, .home-categories-grid a');
    if (catTile) {
      getPostHog().capture('homepage_tile_clicked', {
        tile_type: 'category',
        url: catTile.getAttribute('href') || null,
        label: catTile.textContent?.trim()?.substring(0, 60) || null,
      });
      return;
    }

    // Product rail card
    const railCard = e.target.closest('.home-product-rail a.product-card-v2, .home-product-rail .pcv2-link');
    if (railCard) {
      const rail = railCard.closest('.home-product-rail');
      getPostHog().capture('homepage_product_clicked', {
        url: railCard.getAttribute('href') || null,
        rail_source: rail?.dataset.source || null,
      });
    }
  });

  // Style Quiz nav item click
  document.querySelectorAll('.style-quiz-nav-item a').forEach(function(link) {
    link.addEventListener('click', function() {
      if (!isPostHogReady()) return;
      getPostHog().capture('style_quiz_nav_clicked');
    });
  });

  // ============================================================
  //  POST-CLICK UX INSTRUMENTATION (2026-07)
  // ============================================================

  // ---------- COLLECTION PRODUCT CARD CLICKED ----------
  // Grid position + section (the ad-hero-products featured zone vs the main grid).
  // Collection pages only; homepage rails and search results have their own events.
  // Kept as-is for existing dashboards. grid_card_clicked (grid-discovery-events.js)
  // is the cross-surface superset and fires on the same clicks — count one, not both.
  document.addEventListener('click', (e) => {
    if (!isPostHogReady()) return;
    if (!window.location.pathname.includes('/collections/')) return;
    if (!(e.target instanceof Element)) return;
    const card = e.target.closest('a.product-card-v2');
    if (!card) return;
    const grid = card.closest('.product-grid');
    if (!grid) return;
    // Everything below runs off the interaction path: the grid-wide
    // querySelectorAll + capture must not contribute to INP on card taps.
    setTimeout(() => {
      const item = card.closest('.product-grid__item');
      const items = grid.querySelectorAll('.product-grid__item');
      const position = item ? Array.prototype.indexOf.call(items, item) + 1 : null;
      const handleMatch = (card.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      getPostHog().capture('collection_product_clicked', {
        product_handle: handleMatch ? handleMatch[1] : null,
        section: card.closest('.ad-hero-products') ? 'featured' : 'grid',
        position: position || null,
        grid_size: items.length || null,
        collection_url: window.location.pathname,
      }, { transport: 'sendBeacon' });
    }, 0);
  });

  // ---------- PDP GALLERY INTERACTED ----------
  // zoom / view_mode are deduped to the FIRST occurrence per pageview.
  // slide_changed is deduped per SLIDE IDENTITY instead, so we can measure
  // which slides (moka hero / room mocks / static marketing slides) people
  // actually reach — the PDP iteration-3 education-slide readout needs it.
  // A hard cap bounds the worst case: 2 (zoom+view_mode) + the slide count.
  const GALLERY_ROOTS =
    'media-gallery, gallery-wall-media-gallery, ai-photos-media-gallery, photo-print-media-gallery';
  const GALLERY_MAX_EVENTS = 12;
  const galleryCaptured = new Set();

  // Resolve a slide element to a stable identity:
  //   slide_role — the gallery's own data-slide-role (moka-hero / frame-mock /
  //     room-mock / video / additional)
  //   slide_kind — refines 'additional' via the mock-image container:
  //     'static:<handle>' (marketing slides), 'scene:<handle>' (lifestyle
  //     mocks), or 'image:<index>' (plain product media — index-keyed so each
  //     distinct photo dedups separately; review note on PR #166)
  const gallerySlideIdentity = (slide, index) => {
    if (!(slide instanceof Element)) return { role: null, kind: null };
    const role = slide.getAttribute('data-slide-role');
    if (role && role !== 'additional') return { role, kind: role };
    const mock = slide.querySelector('.mock-image-container');
    if (!mock) {
      return { role, kind: 'image:' + (typeof index === 'number' ? index : 'x') };
    }
    const handle = mock.getAttribute('data-mock-handle') || 'unknown';
    const isStatic = mock.classList.contains('mock-image-container--static');
    return { role, kind: (isStatic ? 'static:' : 'scene:') + handle };
  };

  const captureGalleryInteraction = (interaction, value, extra) => {
    if (!isPostHogReady() || galleryCaptured.size >= GALLERY_MAX_EVENTS) return;
    const dedupKey = extra?.slide_kind
      ? interaction + ':' + extra.slide_kind
      : interaction;
    if (galleryCaptured.has(dedupKey)) return;
    galleryCaptured.add(dedupKey);
    getPostHog().capture('gallery_interacted', {
      interaction,
      value: value || null,
      product_id: getProductId(),
      pdp_type: getPdpType(),
      pdp_variant: getPdpVariant(),
      ...(extra || {}),
    });
  };

  document.addEventListener('slideshow:select', (e) => {
    const detail = e.detail || {};
    if (!detail.userInitiated) return;
    if (!(e.target instanceof Element) || !e.target.closest(GALLERY_ROOTS)) return;
    const identity = gallerySlideIdentity(detail.slide, detail.index);
    captureGalleryInteraction('slide_changed', detail.trigger || null, {
      slide_role: identity.role,
      slide_kind: identity.kind,
      slide_index: typeof detail.index === 'number' ? detail.index : null,
    });
  });

  document.addEventListener('zoom-media:selected', (e) => {
    if (!(e.target instanceof Element) || !e.target.closest(GALLERY_ROOTS)) return;
    captureGalleryInteraction('zoom');
  });

  // Capture phase: the room-preview pill router stopPropagation()s bubble-phase
  // clicks on the pill container, so a bubble listener here would never fire.
  document.addEventListener('click', (e) => {
    if (!(e.target instanceof Element)) return;
    const pill = e.target.closest('.pdp-mode-tabs [data-pill-action], .pdp-mode-tab[data-pill-action]');
    if (!pill) return;
    const action = pill.dataset.pillAction || null;
    setTimeout(() => captureGalleryInteraction('view_mode', action), 0);
  }, true);

  // ---------- CONFIGURATOR SEEN (25% session sample) ----------
  // Impression denominator for "saw the configurator vs used it". Sampled to bound
  // volume (~25% of PDP views); analysis divides by sample_rate.
  //
  // GEOMETRY-NEUTRAL BY DESIGN — the trigger must not depend on how tall the
  // configurator is. The original rule (40% of the root visible) made the
  // threshold a function of the arm: a taller configurator needs more scrolling
  // to clear 40% of itself, so the same shopper behaviour produced fewer
  // impressions on the taller arm. Iteration 3 was bitten by exactly this —
  // "configurator reach -26%" was page geometry, not attention, and it took a
  // separate analysis to establish that. Configurator v2 is a different height
  // again, so the metric is redefined here as "the top of the configurator came
  // 120px into the viewport", which is identical work for the shopper whatever
  // the arm renders below it.
  //
  // `root_height_px` is emitted so the geometry difference between arms is a
  // measured quantity rather than an assumption, and `trigger` names the rule so
  // this event is never silently compared against pre-change history.
  (() => {
    // Two selectors, because the wrapper is not guaranteed shared. Today both
    // configurators render inside `.pdp-surgical-config-root` (configurator v2
    // mounts as a child of that same div), so the first match is the wrapper in
    // both arms and the geometry stays comparable. The v2 tag is named as a
    // fallback anyway: if that markup is ever restructured, the impression
    // denominator would otherwise vanish from the treatment arm SILENTLY —
    // no error, just a missing metric, which is the failure shape this whole
    // change exists to stop repeating.
    const configRoot = document.querySelector('.pdp-surgical-config-root, pdp-configurator-v2');
    if (!configRoot || !('IntersectionObserver' in window)) return;
    const CONFIG_SEEN_RATE = 0.25;
    const SEEN_DEPTH_PX = 120;
    let sampled = false;
    try {
      let flag = sessionStorage.getItem('fy_cfgseen_sample');
      if (flag === null) {
        flag = Math.random() < CONFIG_SEEN_RATE ? '1' : '0';
        sessionStorage.setItem('fy_cfgseen_sample', flag);
      }
      sampled = flag === '1';
    } catch (err) {
      return; // no sessionStorage → skip rather than over-sample
    }
    if (!sampled) return;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      if (!isPostHogReady()) return;
      getPostHog().capture('configurator_seen', {
        product_id: getProductId(),
        pdp_type: getPdpType(),
        pdp_variant: getPdpVariant(),
        sample_rate: CONFIG_SEEN_RATE,
        config_ui: getConfigUi(),
        format: getFormat(),
        trigger: 'top_edge_' + SEEN_DEPTH_PX + 'px',
        root_height_px: Math.round(configRoot.getBoundingClientRect().height),
      });
      // `threshold: 0` + a negative bottom margin fires when the root's TOP edge
      // is SEEN_DEPTH_PX inside the viewport, independent of the root's height.
    }, { threshold: 0, rootMargin: '0px 0px -' + SEEN_DEPTH_PX + 'px 0px' });
    observer.observe(configRoot);
  })();

  // ---------- RAGE CLICK (all sessions, capped 2/pageview) ----------
  // 3+ pointerdowns within 800ms inside a 30px radius. Hand-rolled so we don't
  // need PostHog autocapture (kept off for cost).
  let recentClicks = [];
  let rageCaptured = 0;
  document.addEventListener('pointerdown', (e) => {
    if (rageCaptured >= 2) return;
    const now = performance.now();
    recentClicks = recentClicks.filter((c) => now - c.t < 800);
    recentClicks.push({ t: now, x: e.clientX, y: e.clientY });
    if (recentClicks.length < 3) return;
    const [first] = recentClicks.slice(-3);
    const clustered = recentClicks
      .slice(-3)
      .every((c) => Math.hypot(c.x - first.x, c.y - first.y) < 30);
    if (!clustered) return;
    recentClicks = [];
    rageCaptured++;
    if (!isPostHogReady()) return;
    const target = e.target;
    // element inspection + capture deferred off the interaction path (INP)
    setTimeout(() => {
      const el = target instanceof Element ? target.closest('a, button, [role="button"]') || target : null;
      getPostHog().capture('rage_click', {
        element: el ? el.tagName.toLowerCase() : null,
        element_class: el && typeof el.className === 'string'
          ? el.className.trim().split(/\s+/).slice(0, 2).join(' ') || null
          : null,
        element_text: el?.textContent?.trim()?.replace(/\s+/g, ' ')?.slice(0, 40) || null,
        path: window.location.pathname,
      }, { transport: 'sendBeacon' });
    }, 0);
  }, { capture: true, passive: true });
})();
